import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

// MENEZ_LETTERS_PURE_START
function mzLettersProperties(item) {
  return item?.properties && typeof item.properties === 'object' ? item.properties : {};
}

function mzLettersSupplementId(properties) {
  return String(properties._mz_letters_supplement_variant || properties._mz_letters_supplement || '');
}

function mzLettersDuoId(properties) {
  return String(properties._mz_letters_duo_variant || '');
}

function mzLettersIsCompanion(item) {
  return ['supplement', 'duo_wallet'].includes(mzLettersProperties(item)._mz_letters_role);
}

function mzLettersGroupItems(cart, group) {
  return (cart.items || []).filter((item) => String(mzLettersProperties(item)._mz_letters_group || '') === group);
}

function mzLettersCompanionUrl(cart, item, origin = 'https://menezstudio.com') {
  if (!mzLettersIsCompanion(item)) return '';
  const group = String(mzLettersProperties(item)._mz_letters_group || '');
  const bag = mzLettersGroupItems(cart, group).find((member) => mzLettersProperties(member)._mz_letters_role === 'bag');
  if (!bag) return '';
  const model = bag.product_title || bag.title || bag.handle || '';
  const size = /mini/i.test(model) ? 'mini' : /magnum/i.test(model) ? 'magnum' : '';
  try {
    const url = new URL(bag.url || (size ? '/products/letters-' + size : ''), origin);
    if (url.origin !== new URL(origin).origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) return '';
    if (mzLettersProperties(item)._mz_letters_role === 'duo_wallet') {
      if (!size) return '';
      url.pathname = url.pathname.replace(/\/products\/[^/]+\/?$/, '/products/carterita-letters-' + size);
      // Standalone variant IDs have not been confirmed: never reuse a technical ID.
      url.search = '';
    } else {
      const variantId = bag.variant_id || bag.id;
      if (variantId) url.searchParams.set('variant', String(variantId));
    }
    url.hash = '';
    return url.pathname + url.search;
  } catch {
    return '';
  }
}

function mzLettersValidateCart(cart) {
  const groups = new Map();
  const invalid = [];
  for (const item of cart.items || []) {
    const properties = mzLettersProperties(item);
    const group = String(properties._mz_letters_group || '');
    const role = properties._mz_letters_role;
    if (!group) {
      if (mzLettersIsCompanion(item)) invalid.push({ group: '', items: [item], reason: 'missing-group' });
      continue;
    }
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push(item);
  }
  for (const [group, items] of groups) {
    const bags = items.filter((item) => mzLettersProperties(item)._mz_letters_role === 'bag');
    const supplements = items.filter((item) => mzLettersProperties(item)._mz_letters_role === 'supplement');
    const duoWallets = items.filter((item) => mzLettersProperties(item)._mz_letters_role === 'duo_wallet');
    const companions = items.filter(mzLettersIsCompanion);
    let reason = '';
    if (bags.length !== 1 || bags.length + companions.length !== items.length) reason = 'invalid-members';
    else {
      const bag = bags[0];
      const properties = mzLettersProperties(bag);
      const expectedSupplement = mzLettersSupplementId(properties);
      const expectedSupplementIds = expectedSupplement ? expectedSupplement.split(',') : [];
      const expectedDuo = mzLettersDuoId(properties);
      const expectedDuoColor = String(properties._mz_letters_duo_color || '');
      const expectedTotal = Number(properties._mz_letters_expected_total);
      if (!Number.isSafeInteger(expectedTotal) || expectedTotal <= 0) reason = 'missing-total';
      else if (supplements.length !== expectedSupplementIds.length) reason = 'missing-supplement';
      else if (new Set(expectedSupplementIds).size !== expectedSupplementIds.length || expectedSupplementIds.length > 2 ||
        supplements.map(item => String(item.variant_id || item.id)).sort().join(',') !== [...expectedSupplementIds].sort().join(','))
        reason = 'wrong-supplement';
      else if (expectedDuo ? duoWallets.length !== 1 : duoWallets.length !== 0) reason = 'missing-duo-wallet';
      else if (expectedDuo && String(duoWallets[0].variant_id || duoWallets[0].id) !== expectedDuo)
        reason = 'wrong-duo-wallet';
      else if (expectedDuo ? !expectedDuoColor.trim() : !!expectedDuoColor) reason = 'wrong-duo-color';
      else if (!Number.isSafeInteger(bag.quantity) || bag.quantity < 1 || companions.some((item) => item.quantity !== bag.quantity))
        reason = 'different-quantities';
      else if (companions.some((item) => {
        const companionProperties = mzLettersProperties(item);
        return Number(companionProperties._mz_letters_expected_total) !== expectedTotal ||
          mzLettersSupplementId(companionProperties) !== expectedSupplement ||
          mzLettersDuoId(companionProperties) !== expectedDuo ||
          String(companionProperties._mz_letters_duo_color || '') !== expectedDuoColor;
      })) reason = 'different-configuration';
      // Shopify's original_price is the unit price before any legitimate cart discount.
      else if (items.reduce((total, item) => total + Number(item.original_price ?? item.price), 0) !== expectedTotal)
        reason = 'different-price';
    }
    if (reason) invalid.push({ group, items, reason });
  }
  return invalid;
}

function mzLettersQuantityUpdates(cart, item, quantity) {
  if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Invalid quantity');
  const group = String(mzLettersProperties(item)._mz_letters_group || '');
  if (!group) return null;
  const items = mzLettersGroupItems(cart, group);
  if (quantity > 0 && mzLettersValidateCart({ items }).length) throw new Error('Incomplete Letters configuration');
  return Object.fromEntries(items.map((member) => [member.key, quantity]));
}

function mzLettersOrphanUpdates(cart) {
  const updates = {};
  for (const item of cart.items || []) {
    const properties = mzLettersProperties(item);
    if (!mzLettersIsCompanion(item)) continue;
    const group = String(properties._mz_letters_group || '');
    const bags = group ? mzLettersGroupItems(cart, group).filter((member) => mzLettersProperties(member)._mz_letters_role === 'bag') : [];
    if (!bags.length) updates[item.key] = 0;
    // A capped bag quantity must not leave a larger, chargeable accessory quantity.
    else if (bags.length === 1 && item.quantity > bags[0].quantity) updates[item.key] = bags[0].quantity;
  }
  return updates;
}
// MENEZ_LETTERS_PURE_END

let mzLettersCartQueue = Promise.resolve();
let mzLettersCheckoutPending = false;
let mzLettersCheckoutBypass = false;

function mzLettersQueueMutation(operation) {
  const result = mzLettersCartQueue.then(operation);
  mzLettersCartQueue = result.catch(() => {});
  return result;
}

function mzLettersCartRoute(file) {
  const base = new URL(Theme.routes.cart_change_url, window.location.origin);
  base.pathname = base.pathname.replace(/\/change(?:\.js)?\/?$/, file === 'cart.js' ? '.js' : '/' + file);
  return base.pathname + base.search;
}

async function mzLettersReadCart() {
  const response = await fetch(mzLettersCartRoute('cart.js'), {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Could not read cart');
  return response.json();
}

function mzLettersSections() {
  return [...new Set(Array.from(document.querySelectorAll('cart-items-component')).map((element) => element.dataset.sectionId).filter(Boolean))];
}

async function mzLettersUpdate(updates, sections) {
  const response = await fetch(mzLettersCartRoute('update.js'), fetchConfig('json', {
    body: JSON.stringify({ updates, sections: sections.join(','), sections_url: window.location.pathname }),
  }));
  const cart = await response.json();
  if (!response.ok || cart.errors || cart.status) throw new Error(typeof cart.errors === 'string' ? cart.errors : 'Could not update cart');
  return cart;
}

function mzLettersRenderCart(cart) {
  const source = document.querySelector('cart-items-component');
  if (source) source.dispatchEvent(new CartUpdateEvent({}, source.dataset.sectionId, {
    itemCount: cart.item_count,
    source: 'menez-letters-cart',
    sections: cart.sections,
  }));
  for (const [sectionId, html] of Object.entries(cart.sections || {})) {
    if (typeof html === 'string') morphSection(sectionId, html);
  }
  mzLettersScheduleDecoration(cart);
}

function mzLettersClearErrors() {
  document.querySelectorAll('[data-mz-letters-cart-error]').forEach((element) => element.remove());
}

let mzLettersDecorationFrame = 0;
let mzLettersDecorationRevision = 0;

function mzLettersDecorateCart(cart) {
  const itemsByKey = new Map((cart.items || []).map((item) => [item.key, item]));
  const en = document.documentElement.lang.startsWith('en');
  document.querySelectorAll('cart-items-component [data-key]').forEach((row) => {
    const item = itemsByKey.get(row.dataset.key);
    if (!item || !mzLettersIsCompanion(item)) return;
    const companionUrl = mzLettersCompanionUrl(cart, item, window.location.origin);
    row.querySelectorAll('a[href]').forEach((link) => {
      try {
        if (!/\/products\//.test(new URL(link.getAttribute('href'), window.location.origin).pathname)) return;
        if (companionUrl) link.setAttribute('href', companionUrl);
        else link.removeAttribute('href');
      } catch {
        link.removeAttribute('href');
      }
    });
    row.querySelectorAll('quantity-selector-component, .cart-items__remove').forEach((control) => {
      control.hidden = true;
      control.setAttribute('aria-hidden', 'true');
      control.style.setProperty('display', 'none', 'important');
      if (control instanceof HTMLButtonElement) control.disabled = true;
      control.querySelectorAll('button').forEach((button) => { button.disabled = true; });
      // Keep updates[] enabled so the native cart form retains one quantity per line.
      control.querySelectorAll('input').forEach((input) => { input.readOnly = true; input.tabIndex = -1; });
    });
    const quantityCell = row.querySelector('.cart-items__quantity');
    if (quantityCell && !quantityCell.querySelector('[data-mz-letters-quantity-note]')) {
      const note = document.createElement('p');
      note.dataset.mzLettersQuantityNote = '';
      note.style.cssText = 'margin:4px 0;font-size:12px;line-height:1.5;color:#75695f';
      note.textContent = en ? 'The quantity updates with your Letters.' : 'La cantidad se actualiza con tu Letters.';
      quantityCell.append(note);
    }
    const title = row.querySelector('.cart-items__title');
    if (title) {
      const group = String(mzLettersProperties(item)._mz_letters_group || '');
      const bag = mzLettersGroupItems(cart, group).find((member) => mzLettersProperties(member)._mz_letters_role === 'bag');
      const model = /mini/i.test(bag?.product_title || bag?.title || '') ? 'Letters Mini' : /magnum/i.test(bag?.product_title || bag?.title || '') ? 'Letters Magnum' : 'Letters';
      if (mzLettersProperties(item)._mz_letters_role === 'duo_wallet') {
        const size = /mini/i.test(model) ? 'Mini' : /magnum/i.test(model) ? 'Magnum' : '';
        const color = String(mzLettersProperties(item)._mz_letters_duo_color || '');
        title.textContent = [en ? 'Dúo wallet' : 'Carterita Dúo', size, color].filter(Boolean).join(' · ');
      } else title.textContent = en ? 'Straps for ' + model : 'Bandoleras para ' + model;
    }
  });
}

function mzLettersScheduleDecoration(cart) {
  const revision = ++mzLettersDecorationRevision;
  if (mzLettersDecorationFrame) cancelAnimationFrame(mzLettersDecorationFrame);
  mzLettersDecorationFrame = requestAnimationFrame(async () => {
    mzLettersDecorationFrame = 0;
    try {
      const current = cart || await mzLettersReadCart();
      if (revision === mzLettersDecorationRevision) mzLettersDecorateCart(current);
    } catch (error) {
      // A failed decorative read must not change cart contents or prevent a retry.
      console.error(error);
    }
  });
}

function mzLettersShowError(invalid = [], failed = false) {
  mzLettersClearErrors();
  const en = document.documentElement.lang.startsWith('en');
  const source = Array.from(document.querySelectorAll('cart-items-component')).find((element) => element.getClientRects().length) || document.querySelector('cart-items-component') || document.body;
  const box = document.createElement('div');
  box.dataset.mzLettersCartError = '';
  box.setAttribute('role', 'alert');
  box.setAttribute('tabindex', '-1');
  box.style.cssText = 'padding:16px;margin:12px 0;border:1px solid #b5a397;color:#49382e;font:14px/1.6 Arial,sans-serif';
  const message = document.createElement('p');
  message.textContent = failed
    ? (en ? 'We could not check your cart. Please try again before continuing.' : 'No hemos podido comprobar tu carrito. Inténtalo de nuevo antes de continuar.')
    : (en ? 'A Letters configuration is incomplete. Please review your cart before continuing.' : 'Una configuración de Letters está incompleta. Revisa el carrito antes de continuar.');
  box.append(message);
  if (invalid.length) {
    const titles = [...new Set(invalid.flatMap((entry) => entry.items).filter((item) => mzLettersProperties(item)._mz_letters_role === 'bag').map((item) => item.product_title || item.title))];
    if (titles.length) {
      const detail = document.createElement('p');
      detail.textContent = titles.join(' · ');
      box.append(detail);
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = en ? 'Remove incomplete configuration' : 'Retirar configuración incompleta';
    button.addEventListener('click', () => {
      button.disabled = true;
      mzLettersQueueMutation(async () => {
        const current = await mzLettersReadCart();
        const currentInvalid = mzLettersValidateCart(current);
        const updates = Object.fromEntries(currentInvalid.flatMap((entry) => entry.items).map((item) => [item.key, 0]));
        if (!Object.keys(updates).length) return;
        const cart = await mzLettersUpdate(updates, mzLettersSections());
        mzLettersRenderCart(cart);
      }).then(mzLettersClearErrors).catch(() => mzLettersShowError([], true));
    });
    box.append(button);
  }
  source.prepend(box);
  box.focus();
}

function mzLettersCheckoutAction(event) {
  const checkoutControl = event.target instanceof Element ? event.target.closest('[name="checkout"], [data-mz-letters-checkout], a[href]') : null;
  if (event.type === 'click') {
    if (checkoutControl?.matches('[name="checkout"], [data-mz-letters-checkout]')) return checkoutControl;
    if (checkoutControl instanceof HTMLAnchorElement) {
      const url = new URL(checkoutControl.href, location.href);
      if (url.origin === location.origin && /\/checkout(?:\/|$)/.test(url.pathname)) return checkoutControl;
    }
    return null;
  }
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return null;
  if (event.submitter?.matches('[name="checkout"], [data-mz-letters-checkout]')) return form;
  if (/\/checkout(?:\/|$)/.test(new URL(form.action, location.href).pathname)) return form;
  return null;
}

async function mzLettersGuardCheckout(event) {
  if (mzLettersCheckoutBypass) return;
  const action = mzLettersCheckoutAction(event);
  if (!action) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (mzLettersCheckoutPending) return;
  mzLettersCheckoutPending = true;
  const submitter = event.submitter;
  try {
    await mzLettersCartQueue;
    const cart = await mzLettersReadCart();
    const invalid = mzLettersValidateCart(cart);
    if (invalid.length) {
      mzLettersShowError(invalid);
      return;
    }
    mzLettersClearErrors();
    mzLettersCheckoutBypass = true;
    if (action instanceof HTMLFormElement) {
      const button = submitter || action.querySelector('[name="checkout"]');
      if (button && button.form === action && !button.disabled) action.requestSubmit(button);
      else action.requestSubmit();
    } else action.click();
  } catch (error) {
    console.error(error);
    mzLettersShowError([], true);
  } finally {
    mzLettersCheckoutBypass = false;
    mzLettersCheckoutPending = false;
  }
}

document.addEventListener('click', mzLettersGuardCheckout, true);
document.addEventListener('submit', mzLettersGuardCheckout, true);

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    mzLettersScheduleDecoration();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    // Capture the stable key before the native removal animation removes its row.
    const requestedKey = this.refs.cartItemRows[line - 1]?.dataset.key;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    cartTotal?.shimmer();

    return mzLettersQueueMutation(async () => {
      const current = await mzLettersReadCart();
      const item = requestedKey ? current.items.find((entry) => entry.key === requestedKey) : current.items[line - 1];
      if (!item) {
        // The same action may already have been processed by the other cart surface.
        return mzLettersUpdate({}, Array.from(sectionsToUpdate));
      }
      const updates = mzLettersQuantityUpdates(current, item, Number(quantity));
      let cart;
      if (updates) {
        cart = await mzLettersUpdate(updates, Array.from(sectionsToUpdate));
      } else {
        const body = JSON.stringify({
          ...(requestedKey ? { id: requestedKey } : { line }),
          quantity,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });
        const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
        cart = await response.json();
      }
      if (!cart.errors && !cart.status) {
        const orphanUpdates = mzLettersOrphanUpdates(cart);
        if (Object.keys(orphanUpdates).length) cart = await mzLettersUpdate(orphanUpdates, Array.from(sectionsToUpdate));
      }
      return cart;
    })
      .then((parsedResponseText) => {

        resetShimmer(this);

        if (parsedResponseText.errors || parsedResponseText.status) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        mzLettersScheduleDecoration(parsedResponseText);
        const invalid = mzLettersValidateCart(parsedResponseText);
        if (invalid.length) mzLettersShowError(invalid);
        else mzLettersClearErrors();
      })
      .catch(async (error) => {
        console.error(error);
        resetShimmer(this);
        try {
          await sectionRenderer.renderSection(this.sectionId, { cache: false });
          const current = await mzLettersReadCart();
          const invalid = mzLettersValidateCart(current);
          mzLettersShowError(invalid, !invalid.length);
        } catch {
          mzLettersShowError([], true);
        }
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      Promise.resolve(sectionRenderer.renderSection(this.sectionId, { cache: false })).then(() => mzLettersScheduleDecoration());
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      mzLettersScheduleDecoration();
    } else {
      Promise.resolve(sectionRenderer.renderSection(this.sectionId, { cache: false })).then(() => mzLettersScheduleDecoration());
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
